import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Map, { Marker, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as Sentry from "@sentry/react";
import { SearchError } from "../../errors/sentryErrors";

const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

const GEOCODE_CACHE_KEY = 'roammyway_geocode_cache_v4';
const GEOCODE_MIN_INTERVAL_MS = 250;

const loadGeocodeCache = () => {
  try {
    const raw = localStorage.getItem(GEOCODE_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const geocodeCache = loadGeocodeCache();
const inflightRequests = {};
let geocodeQueue = Promise.resolve();
let lastGeocodeAt = 0;

const persistGeocodeCache = () => {
  try {
    localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(geocodeCache));
  } catch {
  }
};

const cacheKeyFor = (query, expectedCountry) =>
  `${(query || '').toLowerCase().trim()}::${(expectedCountry || '').toLowerCase().trim()}`;

const enqueueGeocode = (fn) => {
  const run = geocodeQueue.then(async () => {
    const wait = Math.max(0, GEOCODE_MIN_INTERVAL_MS - (Date.now() - lastGeocodeAt));
    if (wait > 0) await new Promise(r => setTimeout(r, wait));
    try {
      return await fn();
    } finally {
      lastGeocodeAt = Date.now();
    }
  });
  geocodeQueue = run.catch(() => { });
  return run;
};

const buildDisplayName = (props) => {
  if (!props) return '';
  return [props.name, props.city, props.state, props.country]
    .filter((v, i, arr) => v && arr.indexOf(v) === i)
    .join(', ');
};

const tryGeocode = async (query, expectedCountry = null) => {
  const key = cacheKeyFor(query, expectedCountry);

  if (Object.prototype.hasOwnProperty.call(geocodeCache, key)) {
    return geocodeCache[key];
  }
  if (inflightRequests[key]) {
    return inflightRequests[key];
  }

  const promise = enqueueGeocode(async () => {
    try {
      const response = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5`
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const features = Array.isArray(data?.features) ? data.features : [];

      const toResult = (feature) => {
        const [lng, lat] = feature.geometry?.coordinates || [];
        if (typeof lng !== 'number' || typeof lat !== 'number') return null;
        const fullName = buildDisplayName(feature.properties);
        return {
          lng,
          lat,
          name: feature.properties?.name || fullName.split(',')[0] || '',
          fullName
        };
      };

      if (features.length > 0) {
        if (expectedCountry) {
          const countryLower = expectedCountry.toLowerCase();
          const matchInCountry = features.find(f => {
            const c = (f.properties?.country || '').toLowerCase();
            return c.includes(countryLower) || countryLower.includes(c);
          });
          if (matchInCountry) {
            const result = toResult(matchInCountry);
            if (result) {
              geocodeCache[key] = result;
              persistGeocodeCache();
              return result;
            }
          }
          // expectedCountry set but no match found — do NOT cache or return wrong-country result
          geocodeCache[key] = null;
          persistGeocodeCache();
          return null;
        }

        const result = toResult(features[0]);
        if (result) {
          geocodeCache[key] = result;
          persistGeocodeCache();
          return result;
        }
      }

      geocodeCache[key] = null;
      persistGeocodeCache();
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "search");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        Sentry.captureException(new SearchError(error instanceof Error ? error.message : String(error)));
      });
      console.error('Geocoding error for query:', query, error);
    }
    return null;
  }).finally(() => {
    delete inflightRequests[key];
  });

  inflightRequests[key] = promise;
  return promise;
};

const extractCountryFromDestination = (destination) => {
  if (!destination) return null;

  const parts = destination.split(',').map(p => p.trim());

  const countries = ['India', 'USA', 'United States', 'UK', 'United Kingdom', 'France', 'Germany',
    'Italy', 'Spain', 'Japan', 'Thailand', 'Singapore', 'Malaysia', 'Indonesia', 'Vietnam',
    'Australia', 'New Zealand', 'Canada', 'UAE', 'Dubai', 'Switzerland', 'Netherlands'];

  for (const part of parts) {
    if (countries.some(c => part.toLowerCase() === c.toLowerCase())) {
      return part;
    }
  }

  if (parts.length >= 2) {
    return parts[parts.length - 1];
  }

  return null;
};

const geocodeCity = async (cityName, destinationContext = '', destinationData = null) => {
  if (!cityName) return null;

  const cleanCityName = cityName.replace(/\s*\(Day[s]?\s*\d+[-–]\d+\)/gi, '').trim();

  const state = destinationData?.state || '';
  let country = destinationData?.country || '';

  if (!country && destinationContext) {
    country = extractCountryFromDestination(destinationContext) || '';
  }

  const indianPlaces = ['spiti', 'ladakh', 'leh', 'manali', 'shimla', 'dharamshala', 'rishikesh',
    'haridwar', 'jaipur', 'udaipur', 'jodhpur', 'goa', 'kerala', 'munnar', 'ooty', 'coorg',
    'darjeeling', 'sikkim', 'gangtok', 'varanasi', 'agra', 'delhi', 'mumbai', 'bangalore',
    'chennai', 'kolkata', 'hyderabad', 'pune', 'ahmedabad', 'srinagar', 'kashmir', 'kaza',
    'kalpa', 'nako', 'tabo', 'dhankar', 'kibber', 'chandratal', 'rohtang', 'keylong'];

  if (indianPlaces.some(place => cleanCityName.toLowerCase().includes(place))) {
    country = 'India';
  }

  const queriesToTry = [];

  if (state && country) {
    queriesToTry.push(`${cleanCityName}, ${state}, ${country}`);
  }

  if (country) {
    queriesToTry.push(`${cleanCityName}, ${country}`);
  }

  if (destinationContext && !queriesToTry.some(q => q.includes(destinationContext))) {
    queriesToTry.push(`${cleanCityName}, ${destinationContext}`);
  }

  queriesToTry.push(cleanCityName);

  for (const query of queriesToTry) {
    const result = await tryGeocode(query, country);
    if (result) {
      if (country && result.fullName) {
        const resultCountry = result.fullName.toLowerCase();
        if (resultCountry.includes(country.toLowerCase())) {
          return result;
        }
        console.warn(`✗ Geocoded "${cleanCityName}" but got wrong country: ${result.fullName}, expected: ${country}`);
        continue;
      }
      return result;
    }
  }

  console.error(`✗ Failed to geocode: ${cleanCityName}`);
  return null;
};

const CityMarker = ({ city, index, isActive, isStart, isEnd, isReturn, isGateway, onClick }) => {
  const getMarkerColor = () => {
    if (isStart) return 'bg-emerald-500';
    if (isReturn) return 'bg-emerald-500 ring-2 ring-rose-400';
    if (isEnd) return 'bg-rose-500';
    if (isGateway) return 'bg-white border-2 border-amber-400';
    if (isActive) return 'bg-teal-500';
    return 'bg-white border-2 border-teal-500';
  };

  const getTextColor = () => {
    if (isStart || isEnd || isActive || isReturn) return 'text-white';
    if (isGateway) return 'text-amber-600';
    return 'text-teal-600';
  };

  const getLabel = () => {
    if (isReturn) return '🏠';
    if (isGateway) return '✈';
    return index;
  };

  const sizeClass = isGateway ? 'w-5 h-5' : 'w-7 h-7';
  const textSizeClass = isGateway ? 'text-[10px]' : 'text-xs';

  return (
    <div className="relative cursor-pointer group" onClick={onClick}>
      <div className={`
        ${sizeClass} rounded-full flex items-center justify-center shadow-md
        transition-transform duration-200 group-hover:scale-110
        ${getMarkerColor()}
        ${isActive ? 'ring-4 ring-teal-200' : ''}
      `}>
        <span className={`${textSizeClass} font-bold ${getTextColor()}`}>
          {getLabel()}
        </span>
      </div>

      <div className={`
        absolute left-1/2 -translate-x-1/2 top-full mt-1
        px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap
        transition-all duration-200 shadow-sm
        ${isActive
          ? 'opacity-100 bg-gray-800 text-white'
          : 'opacity-0 group-hover:opacity-100 bg-white text-gray-700 border border-gray-200'}
      `}>
        {isReturn ? `Return to ${city.name}` : isGateway ? `Gateway via ${city.name}` : city.name}
        {city.nights > 0 && <span className="ml-1 text-teal-400">• {city.nights}N</span>}
      </div>
    </div>
  );
};

const ItineraryMap = ({ citiesBreakdown: rawCitiesBreakdown, startingFrom, destination, destinationData, cachedMapData, onMapDataGenerated, isMultiDest = false, userDestinations = [], startingLocationData = null }) => {
  const mapRef = useRef(null);
  const [cities, setCities] = useState([]);
  const [activeCity, setActiveCity] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  // WebGL context creation can fail (GPU disabled/sandboxed browser, old
  // hardware, etc.) — maplibre never fires onLoad in that case, so without
  // this the component would show a blank box forever. onError catches it
  // and lets us show a clear message instead.
  const [mapError, setMapError] = useState(false);
  const animationRef = useRef(null);
  const prevCitiesRef = useRef(null);

  const citiesBreakdown = useMemo(() => {
    if (!rawCitiesBreakdown) return [];
    return rawCitiesBreakdown.filter(city =>
      // city.type !== 'theme' && city.city?.toLowerCase() !== 'presidency'
      city.type !== 'theme' &&
      city.type !== 'arrival' &&
      city.type !== 'departure' &&
      city.city?.toLowerCase() !== 'presidency'
    );
  }, [rawCitiesBreakdown]);

  const getCitiesKey = (breakdown) => {
    if (!breakdown || !Array.isArray(breakdown)) return '';
    return breakdown.map(c => `${c.city}-${c.nights}`).join('|');
  };

  useEffect(() => {
    const findUserCoords = (cityName) => {
      if (!userDestinations?.length || !cityName) return null;
      const lower = cityName.toLowerCase().trim();
      let match = userDestinations.find(d => d.name?.toLowerCase().trim() === lower);
      if (!match) match = userDestinations.find(d => d.name?.toLowerCase().trim().startsWith(lower + ',') || d.name?.toLowerCase().trim() === lower);
      if (!match) match = userDestinations.find(d => lower.startsWith(d.name?.toLowerCase().split(',')[0]?.trim() || '~~~'));
      if (!match) match = userDestinations.find(d => d.name?.toLowerCase().includes(lower) || lower.includes(d.name?.toLowerCase().split(',')[0]?.trim() || '~~~'));
      if (match?.lat != null && match?.lon != null) {
        return { lat: match.lat, lng: match.lon, name: cityName };
      }
      return null;
    };

    const buildCitiesList = async () => {
      const currentCitiesKey = getCitiesKey(citiesBreakdown);
      const cachedCitiesKey = cachedMapData ? getCitiesKey(cachedMapData.map(c => ({ city: c.name, nights: c.nights }))) : '';

      const hasUserCoords = userDestinations?.some(d => d.lat != null && d.lon != null);
      if (!hasUserCoords && cachedMapData && cachedMapData.length > 0 && currentCitiesKey === cachedCitiesKey) {
        setCities(cachedMapData);
        setIsLoading(false);
        prevCitiesRef.current = currentCitiesKey;
        return;
      }

      if (prevCitiesRef.current === currentCitiesKey && cities.length > 0) {
        return;
      }

      setIsLoading(true);
      const citiesList = [];

      if (startingFrom) {
        const startName = startingFrom.split(',')[0].trim();
        const firstDestCity = citiesBreakdown?.[0]?.city || destination?.split(',')[0]?.trim();

        if (startName.toLowerCase() !== firstDestCity?.toLowerCase()) {
          let startCoords = null;
          if (startingLocationData?.lat != null && startingLocationData?.lon != null) {
            startCoords = { lat: startingLocationData.lat, lng: startingLocationData.lon, name: startName };
          } else {
            startCoords = await geocodeCity(startingFrom);
          }
          if (startCoords) {
            citiesList.push({
              ...startCoords,
              name: startName,
              isStart: true,
              nights: 0
            });
          }
        }
      }

      if (citiesBreakdown && citiesBreakdown.length > 0) {
        const geocodeWithContext = async (cityName) => {
          if (!isMultiDest) {
            return geocodeCity(cityName, destination, destinationData);
          }
          if (userDestinations?.length) {
            for (const ud of userDestinations) {
              if (!ud.country) continue;
              const result = await geocodeCity(cityName, ud.name, { country: ud.country });
              if (result) return result;
            }
          }
          return geocodeCity(cityName, destination, destinationData);
        };

        for (const city of citiesBreakdown) {
          if (city.type === 'theme' || city.city.toLowerCase() === 'presidency') continue;
          const base = {
            name: city.city,
            nights: city.nights,
            days: city.days,
            type: city.type || null,
          };
          if (city.lng && city.lat) {
            citiesList.push({ ...base, lng: city.lng, lat: city.lat });
          } else {
            const userCoords = findUserCoords(city.city);
            if (userCoords) {
              citiesList.push({ ...base, lng: userCoords.lng, lat: userCoords.lat });
            } else {
              const coords = await geocodeWithContext(city.city);
              if (coords) {
                citiesList.push({ ...base, lng: coords.lng, lat: coords.lat });
              }
            }
          }
        }
      } else if (destination) {
        const userCoords = findUserCoords(destination.split(',')[0].trim());
        const destCoords = userCoords || await geocodeCity(destination);
        if (destCoords) {
          citiesList.push({
            ...destCoords,
            name: destination.split(',')[0].trim(),
            nights: null
          });
        }
      }

      if (startingFrom && citiesList.length > 0) {
        const startName = startingFrom.split(',')[0].trim();
        const lastCity = citiesList[citiesList.length - 1];

        if (lastCity && lastCity.name?.toLowerCase() !== startName.toLowerCase() && !lastCity.isStart) {
          const startCity = citiesList.find(c => c.isStart);

          if (startCity) {
            citiesList.push({
              lng: startCity.lng,
              lat: startCity.lat,
              name: startName,
              isEnd: true,
              isReturn: true,
              nights: 0
            });
          } else {
            let returnCoords = null;
            if (startingLocationData?.lat != null && startingLocationData?.lon != null) {
              returnCoords = { lat: startingLocationData.lat, lng: startingLocationData.lon, name: startName };
            } else {
              returnCoords = await geocodeCity(startingFrom);
            }
            if (returnCoords) {
              citiesList.push({
                ...returnCoords,
                name: startName,
                isEnd: true,
                isReturn: true,
                nights: 0
              });
            }
          }
        }
      }

      setCities(citiesList);
      setIsLoading(false);
      prevCitiesRef.current = getCitiesKey(citiesBreakdown);

      if (citiesList.length > 0 && onMapDataGenerated) {
        onMapDataGenerated(citiesList);
      }
    };

    buildCitiesList();
  }, [citiesBreakdown, startingFrom, destination, destinationData, cachedMapData, onMapDataGenerated, cities.length, userDestinations, startingLocationData]);

  const LINE_COLORS = [
    '#14b8a6', // teal-500
    '#3b82f6', // blue-500
    '#f59e0b', // amber-500
    '#ec4899', // pink-500
    '#8b5cf6', // violet-500
    '#ef4444', // red-500
  ];

  const haversineKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };

  const distanceMarkers = [];
  const routeFeatures = [];

  for (let i = 1; i < cities.length; i++) {
    const prev = cities[i - 1];
    const curr = cities[i];
    const dist = haversineKm(prev.lat, prev.lng, curr.lat, curr.lng);

    distanceMarkers.push({
      lng: (prev.lng + curr.lng) / 2,
      lat: (prev.lat + curr.lat) / 2,
      text: `${dist} km`,
      color: LINE_COLORS[(i - 1) % LINE_COLORS.length]
    });

    routeFeatures.push({
      type: 'Feature',
      properties: {
        color: LINE_COLORS[(i - 1) % LINE_COLORS.length]
      },
      geometry: {
        type: 'LineString',
        coordinates: [[prev.lng, prev.lat], [curr.lng, curr.lat]]
      }
    });
  }

  const flyToCity = useCallback((index) => {
    if (!mapRef.current || !cities[index]) return;
    const city = cities[index];
    mapRef.current.flyTo({
      center: [city.lng, city.lat],
      zoom: 10,
      duration: 1500
    });
    setActiveCity(index);
  }, [cities]);

  const fitAllCities = useCallback(() => {
    if (!mapRef.current || cities.length === 0) return;

    if (cities.length === 1) {
      mapRef.current.flyTo({
        center: [cities[0].lng, cities[0].lat],
        zoom: 9,
        duration: 1000
      });
    } else {
      const lngs = cities.map(c => c.lng);
      const lats = cities.map(c => c.lat);
      const bounds = [
        [Math.min(...lngs) - 0.5, Math.min(...lats) - 0.5],
        [Math.max(...lngs) + 0.5, Math.max(...lats) + 0.5]
      ];
      mapRef.current.fitBounds(bounds, { duration: 1000, padding: 40 });
    }
    setActiveCity(-1);
  }, [cities]);

  useEffect(() => {
    if (mapLoaded && cities.length > 0) {
      setTimeout(fitAllCities, 300);
    }
  }, [mapLoaded, cities.length, fitAllCities]);

  useEffect(() => {
    if (!isPlaying || cities.length === 0) return;

    let currentIdx = 0;
    flyToCity(0);

    animationRef.current = setInterval(() => {
      currentIdx++;
      if (currentIdx >= cities.length) {
        setIsPlaying(false);
        setTimeout(fitAllCities, 500);
      } else {
        flyToCity(currentIdx);
      }
    }, 2000);

    return () => clearInterval(animationRef.current);
  }, [isPlaying, cities.length, flyToCity, fitAllCities]);

  const startTour = () => {
    setIsPlaying(true);
  };

  if (isLoading) {
    return (
      <div className="aspect-[2/1] bg-gray-50 rounded-xl flex items-center justify-center border border-gray-200">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Loading map...</p>
        </div>
      </div>
    );
  }

  if (cities.length === 0) return null;

  if (mapError) {
    // maplibre needs WebGL; some browsers/sandboxed environments refuse to
    // grant a GL context at all (see onError below). OSM's embeddable iframe
    // is plain HTML/tiles — no WebGL required — so it still shows a real map
    // in exactly the situations where the WebGL map cannot render anything.
    const lats = cities.map((c) => c.lat).filter((n) => Number.isFinite(n));
    const lngs = cities.map((c) => c.lng).filter((n) => Number.isFinite(n));
    const padDeg = 0.35;
    const minLat = Math.min(...lats) - padDeg;
    const maxLat = Math.max(...lats) + padDeg;
    const minLng = Math.min(...lngs) - padDeg;
    const maxLng = Math.max(...lngs) + padDeg;
    const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;
    const marker = `${cities[0].lat},${cities[0].lng}`;
    const osmSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${marker}`;

    return (
      <div className="h-72 md:h-96 w-full rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
        <iframe
          title="Trip route map"
          src={osmSrc}
          className="w-full h-full border-0"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div className="relative h-72 md:h-96 w-full rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: cities[0]?.lng || 78.9629,
          latitude: cities[0]?.lat || 20.5937,
          zoom: 5
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={MAP_STYLE}
        onLoad={() => setMapLoaded(true)}
        onError={(e) => {
          console.warn('[ItineraryMap] failed to load:', e?.error?.message || e);
          setMapError(true);
        }}
        attributionControl={false}
      >
        {routeFeatures.length > 0 && (
          <Source
            id="route"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: routeFeatures
            }}
          >
            <Layer
              id="route-line"
              type="line"
              paint={{
                'line-color': ['get', 'color'],
                'line-width': 3,
                'line-opacity': 0.85
              }}
            />
          </Source>
        )}

        {distanceMarkers.map((dm, idx) => (
          <Marker
            key={`dist-${idx}`}
            longitude={dm.lng}
            latitude={dm.lat}
            anchor="center"
          >
            <div
              className="bg-white/95 backdrop-blur px-1.5 py-0.5 rounded shadow text-[10px] font-medium whitespace-nowrap border"
              style={{ color: dm.color, borderColor: dm.color }}
            >
              {dm.text}
            </div>
          </Marker>
        ))}

        {cities.map((city, index) => {
          let overlapIndex = 0;
          const cLat = Number(city.lat).toFixed(4);
          const cLng = Number(city.lng).toFixed(4);
          for (let i = 0; i < index; i++) {
            if (Number(cities[i].lat).toFixed(4) === cLat && Number(cities[i].lng).toFixed(4) === cLng) {
              overlapIndex++;
            }
          }

          const offsetAmount = 14; // pixels
          const xOffset = overlapIndex * offsetAmount;
          const yOffset = overlapIndex * -offsetAmount;

          return (
            <Marker
              key={`${city.name}-${index}-${overlapIndex}`}
              longitude={city.lng}
              latitude={city.lat}
              anchor="center"
              offset={[xOffset, yOffset]}
              style={{ zIndex: overlapIndex }}
            >
              <CityMarker
                city={city}
                index={index + 1}
                isActive={activeCity === index}
                isStart={city.isStart}
                isEnd={city.isEnd || (index === cities.length - 1 && !city.isStart && !city.isReturn && cities.length > 1)}
                isReturn={city.isReturn}
                isGateway={city.type === 'arrival' || city.type === 'departure'}
                onClick={() => {
                  setIsPlaying(false);
                  flyToCity(index);
                }}
              />
            </Marker>
          );
        })}
      </Map>

      <div className="absolute top-3 left-3 flex flex-col gap-1">
        <button
          onClick={() => mapRef.current?.zoomIn({ duration: 300 })}
          className="w-8 h-8 bg-white rounded-lg shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:text-teal-600 transition-colors cursor-pointer"
          title="Zoom In"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
          </svg>
        </button>
        <button
          onClick={() => mapRef.current?.zoomOut({ duration: 300 })}
          className="w-8 h-8 bg-white rounded-lg shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:text-teal-600 transition-colors cursor-pointer"
          title="Zoom Out"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
          </svg>
        </button>
        <div className="h-1" />
        <button
          onClick={() => mapRef.current?.panBy([0, -50], { duration: 300 })}
          className="w-8 h-8 bg-white rounded-lg shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:text-teal-600 transition-colors cursor-pointer"
          title="Pan Up"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <div className="flex gap-1">
          <button
            onClick={() => mapRef.current?.panBy([-50, 0], { duration: 300 })}
            className="w-8 h-8 bg-white rounded-lg shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:text-teal-600 transition-colors cursor-pointer"
            title="Pan Left"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => mapRef.current?.panBy([50, 0], { duration: 300 })}
            className="w-8 h-8 bg-white rounded-lg shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:text-teal-600 transition-colors cursor-pointer"
            title="Pan Right"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <button
          onClick={() => mapRef.current?.panBy([0, 50], { duration: 300 })}
          className="w-8 h-8 bg-white rounded-lg shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:text-teal-600 transition-colors cursor-pointer"
          title="Pan Down"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className="absolute bottom-3 left-3 flex gap-2">
        {cities.length > 1 && (
          <button
            onClick={() => isPlaying ? setIsPlaying(false) : startTour()}
            disabled={isPlaying}
            className="px-3 py-1.5 bg-white rounded-lg shadow text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50"
          >
            {isPlaying ? 'Playing...' : '▶ Play Tour'}
          </button>
        )}
        <button
          onClick={() => { setIsPlaying(false); fitAllCities(); }}
          className="px-3 py-1.5 bg-white rounded-lg shadow text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          ⛶ Fit All
        </button>
      </div>

      <div className="absolute top-3 right-3 flex items-center gap-3 px-3 py-1.5 bg-white/90 backdrop-blur rounded-lg shadow text-xs">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-gray-600">Start</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-teal-500" />
          <span className="text-gray-600">Cities</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-emerald-500 ring-1 ring-rose-400" />
          <span className="text-gray-600">Return</span>
        </div>
      </div>
    </div>
  );
};

export default ItineraryMap;
